// Authentication wrapper for Supabase Auth + the custom_access_token_hook
// JWT claim. Surfaces a typed AuthState ('loading' | 'authenticated' | 'anonymous')
// and signIn / signOut helpers.
//
// Role lookup: the auth hook stamps `app_role` ('staff' | 'ops') into the JWT
// payload. We decode the access token client-side to read it (cheap and
// doesn't need a network round-trip on every render).
import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { AppRole } from '../types';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

// The three hardcoded Access Control superusers. Mirrors public.is_superuser()
// in the DB (migration 83). Frontend gating only — the server re-checks.
export const SUPERUSER_EMAILS: readonly string[] = [
  'tudor.fanaru@arc-robotics.org',
  'derek.wang@arc-robotics.org',
  'natalie.hahn@arc-robotics.org',
];

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  email: string | null;
  /** From the JWT's `app_role` custom claim. Null until the hook stamps it. */
  role: AppRole | null;
  /** Per-user permission grants from JWT claims (default false). */
  l3Override: boolean;
  captainsMeeting: boolean;
  /** Email is one of the three Access Control superusers. */
  isSuperuser: boolean;
}

interface JwtClaims {
  app_role?: AppRole;
  l3_override?: boolean;
  captains_meeting?: boolean;
}

function readClaims(session: Session | null): JwtClaims {
  if (!session) return {};
  // JWT payload is base64url-encoded JSON between two dots.
  try {
    return JSON.parse(atob(session.access_token.split('.')[1]!)) as JwtClaims;
  } catch {
    // Malformed token shouldn't happen in practice; treat as no claims.
    return {};
  }
}

function projectAuth(session: Session | null): AuthState {
  if (!session) {
    return { status: 'anonymous', user: null, email: null, role: null, l3Override: false, captainsMeeting: false, isSuperuser: false };
  }
  const claims = readClaims(session);
  const role = (claims.app_role === 'volunteer' || claims.app_role === 'staff' || claims.app_role === 'ops')
    ? claims.app_role : null;
  const email = session.user.email ?? null;
  return {
    status: 'authenticated',
    user: session.user,
    email,
    role,
    l3Override: claims.l3_override === true,
    captainsMeeting: claims.captains_meeting === true,
    isSuperuser: !!email && SUPERUSER_EMAILS.includes(email.toLowerCase()),
  };
}

export function useAuth(): AuthState & {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>({
    status: 'loading', user: null, email: null, role: null,
    l3Override: false, captainsMeeting: false, isSuperuser: false,
  });

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setState(projectAuth(data.session));
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setState(projectAuth(session));
    });
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // No `hd:` filter — volunteers signing in with personal Gmail
        // accounts need to reach the consent screen too. The auth hook
        // (custom_access_token_hook) gates which addresses get an
        // app_role: arc-robotics.org domain by default, plus anyone in
        // the staff_emails / ops_emails allow-lists. Anyone else lands
        // authenticated-but-roleless and can only do anonymous reads.
        redirectTo: window.location.origin,
      },
    });
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return { ...state, signIn, signOut };
}
